// src/components/admin/pages/TodayJobsReport.js

import React, {
  useEffect,
  useState,
} from "react";

import {
  useLocation,
} from "react-router-dom";

import {
  FiSearch,
  FiDownload,
  FiEdit,
  FiTrash2,
} from "react-icons/fi";

import {
  FaBriefcase,
  FaBuilding,
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaUsers,
  FaMoneyBillWave,
} from "react-icons/fa";

import Swal from "sweetalert2";
import dayjs from "dayjs";

import * as XLSX from "xlsx";

import { jsPDF } from "jspdf";
import "jspdf-autotable";

import {
  getData,
  postData,
} from "../../../services/FetchNodeservices";

export default function TodayJobsReport() {

  /* ================================================= */
  /* LOCATION */
  /* ================================================= */

  const location =
    useLocation();

  /* ================================================= */
  /* STATES */
  /* ================================================= */

  const [rows, setRows] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  const [page, setPage] =
    useState(1);

  const rowsPerPage = 10;

  const [filters, setFilters] =
    useState({

      search: "",

      company: "",

      position: "",

      status: "",

      postedDate: "",
    });

  /* ================================================= */
  /* LOAD DATA */
  /* ================================================= */

  useEffect(() => {

    fetchJobs();

  }, []);

  /* ================================================= */
  /* AUTO TODAY FILTER */
  /* ================================================= */

  useEffect(() => {

    const params =
      new URLSearchParams(
        location.search
      );

    const filter =
      params.get("filter");

    if (
      filter === "today"
    ) {

      setFilters((prev) => ({

        ...prev,

        postedDate:
          dayjs().format(
            "YYYY-MM-DD"
          ),
      }));
    }

  }, [location.search]);

  useEffect(() => {

  const params =
    new URLSearchParams(
      location.search
    );

  const status =
    params.get("status");

  if (status) {

    setFilters((prev) => ({

      ...prev,

      status:
        status.charAt(0)
          .toUpperCase() +
        status.slice(1),
    }));
  }

}, [location.search]);

  /* ================================================= */
  /* FETCH */
  /* ================================================= */

  const fetchJobs =
    async () => {

      try {

        setLoading(true);

        const result =
          await getData(
            "job/jobs"
          );

        if (
          result?.status
        ) {

          setRows(
            result.jobs || []
          );
        }

      } catch (err) {

        Swal.fire(
          "Error",
          "Failed to load jobs",
          "error"
        );

      } finally {

        setLoading(false);
      }
    };

  /* ================================================= */
  /* DROPDOWNS */
  /* ================================================= */

  const companies =
    [
      ...new Set(
        rows.map(
          (r) =>
            r.company
        )
      ),
    ];

  const positions =
    [
      ...new Set(
        rows.map(
          (r) =>
            r.title
        )
      ),
    ];

  const statuses =
    [
      ...new Set(
        rows.map(
          (r) =>
            r.status
        )
      ),
    ];

  /* ================================================= */
  /* FILTER */
  /* ================================================= */

  const filteredData =
    rows.filter(
      (row) => {

        const search =
          filters.search.toLowerCase();

        const matchesSearch =

          row.title
            ?.toLowerCase()
            .includes(
              search
            ) ||

          row.company
            ?.toLowerCase()
            .includes(
              search
            ) ||

          row.location
            ?.toLowerCase()
            .includes(
              search
            );

        const matchesCompany =

          !filters.company ||

          row.company ===
            filters.company;

        const matchesPosition =

          !filters.position ||

          row.title ===
            filters.position;

        const matchesStatus =

          !filters.status ||

          row.status ===
            filters.status;

        const matchesDate =

          !filters.postedDate ||

          dayjs(
            row.created_at
          ).format(
            "YYYY-MM-DD"
          ) ===
            filters.postedDate;

        return (

          matchesSearch &&
          matchesCompany &&
          matchesPosition &&
          matchesStatus &&
          matchesDate
        );
      }
    );

  /* ================================================= */
  /* PAGINATION */
  /* ================================================= */

  const paginated =
    filteredData.slice(

      (page - 1) *
        rowsPerPage,

      page *
        rowsPerPage
    );

  /* ================================================= */
  /* RESET FILTER */
  /* ================================================= */

  const resetFilters =
    () => {

      setFilters({

        search: "",

        company: "",

        position: "",

        status: "",

        postedDate: "",
      });
    };

  /* ================================================= */
  /* EXPORT EXCEL */
  /* ================================================= */

  const exportExcel =
    () => {

      const ws =
        XLSX.utils.json_to_sheet(
          filteredData
        );

      const wb =
        XLSX.utils.book_new();

      XLSX.utils.book_append_sheet(
        wb,
        ws,
        "Jobs Report"
      );

      XLSX.writeFile(
        wb,
        "Jobs_Report.xlsx"
      );
    };

  /* ================================================= */
  /* EXPORT PDF */
  /* ================================================= */

  const exportPDF =
    () => {

      const doc =
        new jsPDF();

      doc.text(
        "Jobs Report",
        14,
        15
      );

      doc.autoTable({

        head: [[

          "Company",

          "Position",

          "Location",

          "Vacancies",

          "Status",
        ]],

        body:
          filteredData.map(
            (r) => ([

              r.company,

              r.title,

              r.location,

              r.vacancies,

              r.status,
            ])
          ),
      });

      doc.save(
        "Jobs_Report.pdf"
      );
    };

  /* ================================================= */
  /* DELETE */
  /* ================================================= */

  const deleteJob =
    async (reqId) => {

      Swal.fire({

        title:
          "Delete Job?",

        icon:
          "warning",

        showCancelButton:
          true,
      }).then(
        async (res) => {

          if (
            res.isConfirmed
          ) {

            await postData(

              `job/delete-job/${reqId}`,

              {}
            );

            fetchJobs();
          }
        }
      );
    };

  return (

    <div
      className="
        p-4
        md:p-6
        bg-[#f8f9fb]
        min-h-screen
        md:ml-72
        mt-14
      "
    >

      {/* ================================================= */}
      {/* HEADER */}
      {/* ================================================= */}

      <div className="flex items-center justify-between mb-6">

        <div>

          <h1 className="text-2xl font-semibold">
            Jobs Report
          </h1>

          <p className="text-sm text-gray-500 mt-1">
            Today's posted job positions & hiring pipeline
          </p>
        </div>

        <div className="flex gap-3">

          <button
            onClick={exportExcel}
            className="
              bg-black
              text-white
              px-4
              py-2
              rounded-2xl
              text-sm
            "
          >
            Excel
          </button>

          <button
            onClick={exportPDF}
            className="
              border
              border-gray-300
              px-4
              py-2
              rounded-2xl
              text-sm
            "
          >
            PDF
          </button>
        </div>
      </div>

      {/* ================================================= */}
      {/* FILTERS */}
      {/* ================================================= */}

      <div
        className="
          bg-white
          border
          border-gray-200
          rounded-3xl
          p-5
          mb-6
          shadow-sm
        "
      >

        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">

          {/* SEARCH */}

          <div
            className="
              flex
              items-center
              gap-3
              border
              border-gray-200
              rounded-2xl
              px-4
              py-3
              bg-[#fafafa]
            "
          >

            <FiSearch className="text-gray-400" />

            <input
              type="text"
              placeholder="Search..."
              value={
                filters.search
              }
              onChange={(e) =>
                setFilters({

                  ...filters,

                  search:
                    e.target.value,
                })
              }
              className="
                w-full
                outline-none
                bg-transparent
                text-sm
              "
            />
          </div>

          {/* DATE */}

          <input
            type="date"
            value={
              filters.postedDate
            }
            onChange={(e) =>
              setFilters({

                ...filters,

                postedDate:
                  e.target.value,
              })
            }
            className="
              border
              border-gray-200
              rounded-2xl
              px-4
              py-3
              text-sm
              bg-[#fafafa]
            "
          />

          {/* COMPANY */}

          <select
            value={
              filters.company
            }
            onChange={(e) =>
              setFilters({

                ...filters,

                company:
                  e.target.value,
              })
            }
            className="
              border
              border-gray-200
              rounded-2xl
              px-4
              py-3
              text-sm
              bg-[#fafafa]
            "
          >

            <option value="">
              All Company
            </option>

            {companies.map(
              (
                item,
                index
              ) => (

                <option
                  key={index}
                  value={item}
                >
                  {item}
                </option>
              )
            )}
          </select>

          {/* POSITION */}

          <select
            value={
              filters.position
            }
            onChange={(e) =>
              setFilters({

                ...filters,

                position:
                  e.target.value,
              })
            }
            className="
              border
              border-gray-200
              rounded-2xl
              px-4
              py-3
              text-sm
              bg-[#fafafa]
            "
          >

            <option value="">
              All Position
            </option>

            {positions.map(
              (
                item,
                index
              ) => (

                <option
                  key={index}
                  value={item}
                >
                  {item}
                </option>
              )
            )}
          </select>

          {/* STATUS */}

          <select
            value={
              filters.status
            }
            onChange={(e) =>
              setFilters({

                ...filters,

                status:
                  e.target.value,
              })
            }
            className="
              border
              border-gray-200
              rounded-2xl
              px-4
              py-3
              text-sm
              bg-[#fafafa]
            "
          >

            <option value="">
              All Status
            </option>

            {statuses.map(
              (
                item,
                index
              ) => (

                <option
                  key={index}
                  value={item}
                >
                  {item}
                </option>
              )
            )}
          </select>
        </div>

        <div className="flex justify-end mt-4">

          <button
            onClick={
              resetFilters
            }
            className="
              border
              border-gray-300
              px-5
              py-2
              rounded-2xl
              text-sm
            "
          >
            Reset Filters
          </button>
        </div>
      </div>

      {/* ================================================= */}
      {/* TABLE */}
      {/* ================================================= */}

      <div
        className="
          bg-white
          border
          border-gray-200
          rounded-3xl
          shadow-sm
          overflow-hidden
        "
      >

        <div className="overflow-x-auto">

          <table className="w-full">

            <thead
              className="
                bg-[#fafafa]
                border-b
                border-gray-200
              "
            >

              <tr>

                {[
                  "Position",
                  "Company",
                  "Location",
                  "Hiring",
                  "Details",
                  "Status",
                  "Action",
                ].map(
                  (head) => (

                    <th
                      key={head}
                      className="
                        px-5
                        py-4
                        text-left
                        text-xs
                        font-semibold
                        text-gray-500
                      "
                    >
                      {head}
                    </th>
                  )
                )}
              </tr>
            </thead>

            <tbody>

              {loading ? (

                <tr>

                  <td
                    colSpan={7}
                    className="
                      text-center
                      py-10
                      text-gray-500
                    "
                  >
                    Loading...
                  </td>
                </tr>

              ) : paginated.length === 0 ? (

                <tr>

                  <td
                    colSpan={7}
                    className="
                      text-center
                      py-10
                      text-gray-500
                    "
                  >
                    No Jobs Found
                  </td>
                </tr>

              ) : (

                paginated.map(
                  (
                    row,
                    index
                  ) => (

                    <tr
                      key={index}
                      className="
                        border-b
                        border-gray-100
                        hover:bg-[#fafafa]
                        transition-all
                      "
                    >

                      {/* POSITION */}

                      <td className="px-5 py-4">

                        <div className="flex items-center gap-3">

                          <div
                            className="
                              w-10
                              h-10
                              rounded-2xl
                              bg-black
                              text-white
                              flex
                              items-center
                              justify-center
                            "
                          >
                            <FaBriefcase />
                          </div>

                          <div>

                            <h3 className="text-sm font-medium">
                              {row.title}
                            </h3>

                            <p className="text-xs text-gray-500">
                              Req ID:
                              {" "}
                              {row.reqId}
                            </p>
                          </div>
                        </div>
                      </td>

                      {/* COMPANY */}

                      <td className="px-5 py-4">

                        <div className="space-y-1">

                          <div className="flex items-center gap-2 text-sm">

                            <FaBuilding className="text-gray-400" />

                            {row.company}
                          </div>

                          <p className="text-xs text-gray-500">
                            {row.postedBy}
                          </p>
                        </div>
                      </td>

                      {/* LOCATION */}

                      <td className="px-5 py-4">

                        <div className="flex items-center gap-2 text-sm">

                          <FaMapMarkerAlt className="text-gray-400" />

                          {row.location}
                        </div>
                      </td>

                      {/* HIRING */}

                      <td className="px-5 py-4">

                        <div className="space-y-1">

                          <div className="flex items-center gap-2 text-sm">

                            <FaUsers className="text-gray-400" />

                            Vacancies:
                            {" "}
                            {row.vacancies}
                          </div>

                          <p className="text-xs text-gray-500">
                            Candidates:
                            {" "}
                            {row.candidates}
                          </p>
                        </div>
                      </td>

                      {/* DETAILS */}

                      <td className="px-5 py-4">

                        <div className="space-y-1">

                          <div className="flex items-center gap-2 text-sm font-medium text-green-700">

                            <FaMoneyBillWave />

                            ₹
                            {Number(
                              row.annualCtc || 0
                            ).toLocaleString()}
                          </div>

                          <div className="flex items-center gap-2 text-xs text-gray-500">

                            <FaCalendarAlt />

                            {
                              dayjs(
                                row.created_at
                              ).format(
                                "YYYY-MM-DD"
                              )
                            }
                          </div>
                        </div>
                      </td>

                      {/* STATUS */}

                      <td className="px-5 py-4">

                        <span
                          className={`
                            inline-flex
                            items-center
                            px-3
                            py-1
                            rounded-full
                            text-xs
                            font-semibold

                            ${
                              row.status === "Active"

                                ? "bg-green-100 text-green-700"

                                : "bg-red-100 text-red-700"
                            }
                          `}
                        >
                          {row.status}
                        </span>
                      </td>

                      {/* ACTION */}

                      <td className="px-5 py-4">

                        <div className="flex gap-2">

                          <button
                            className="
                              w-9
                              h-9
                              rounded-xl
                              bg-black
                              text-white
                              flex
                              items-center
                              justify-center
                            "
                          >
                            <FiEdit size={14} />
                          </button>

                          <button
                            onClick={() =>
                              deleteJob(
                                row.reqId
                              )
                            }
                            className="
                              w-9
                              h-9
                              rounded-xl
                              border
                              border-red-200
                              text-red-500
                              flex
                              items-center
                              justify-center
                            "
                          >
                            <FiTrash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                )
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ================================================= */}
      {/* PAGINATION */}
      {/* ================================================= */}

      <div className="flex justify-end gap-3 mt-5">

        {page > 1 && (

          <button
            onClick={() =>
              setPage(
                page - 1
              )
            }
            className="
              px-4
              py-2
              rounded-2xl
              border
              border-gray-300
              text-sm
            "
          >
            Prev
          </button>
        )}

        {page *
          rowsPerPage <
          filteredData.length && (

          <button
            onClick={() =>
              setPage(
                page + 1
              )
            }
            className="
              px-4
              py-2
              rounded-2xl
              bg-black
              text-white
              text-sm
            "
          >
            Next
          </button>
        )}
      </div>
    </div>
  );
}